/**
 * GuestRoomPage — standalone guest experience for external participants.
 *
 * Rendered when the URL matches /guest/:token (detected in main.tsx).
 * No Eurion account required. Features:
 *  - Name entry form → join room
 *  - Real-time chat via WebSocket
 *  - Video/audio calls via WebRTC signaling
 *  - Screen sharing
 *  - Responsive, works on mobile
 *
 * Uses the same Nord theme as the main app.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Send, PhoneCall, PhoneOff, Mic, MicOff,
  Video, VideoOff, Monitor, MonitorOff, Users,
  LogOut, Shield, Loader2, AlertCircle, ArrowRight,
  Hand, Maximize, MessageSquare, Sparkles, Clock,
} from 'lucide-react';
import { TrustLine } from '../ui/TrustLine';
import { MessageContent } from '../chat/MessageContent';
import { mayCloseProducer } from '../call/peerTracks';
import { FACILITATOR_NAME, FACILITATOR_SENDER_ID, recapToHtml } from '../../lib/aiContext';
import { attachmentOf } from '../../lib/attachments';
import { AttachmentCard } from '../files/AttachmentCard';
import { useIsPhone, watchVisualViewport } from '../../lib/viewport';
import { jittered } from '../../lib/backoff';
import { tv } from '../../lib/i18nText';

// ── Types ────────────────────────────────────────────────────────────────────

interface RoomInfo {
  roomName: string;
  roomTopic?: string;
  roomType: string;
  permissions: Permissions;
  expiresAt: string;
  suggestedName?: string;
}

interface Permissions {
  canChat: boolean;
  canVideo: boolean;
  canScreenShare: boolean;
  canUploadFiles: boolean;
}

interface GuestSession {
  sessionToken: string;
  guestId: string;
  displayName: string;
  roomId: string;
  permissions: Permissions;
}

interface Message {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  type: string;
  metadata: Record<string, unknown>;
  created_at: string;
  sender_name?: string;
  is_guest?: boolean;
  format?: 'text' | 'markdown' | 'html';
}

interface ActiveGuest {
  id: string;
  display_name: string;
  connected_at: string;
}

// ── API helper (guest-specific, no global auth store) ────────────────────────

const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api';

async function guestApi<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
): Promise<{ success: boolean; data?: T; error?: { code: string; message: string } }> {
  const headers: Record<string, string> = {};
  if (body) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  return res.json();
}

// ── STUN servers (fallback — real ICE config including TURN is fetched from API) ──

const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: `stun:${import.meta.env.VITE_TURN_HOST ?? 'turn.eurion.se'}:3478` },
  {
    urls: [
      `turn:${import.meta.env.VITE_TURN_HOST ?? 'turn.eurion.se'}:3478`,
      `turn:${import.meta.env.VITE_TURN_HOST ?? 'turn.eurion.se'}:3478?transport=tcp`,
    ],
    username: import.meta.env.VITE_TURN_USER ?? 'eurion',
    credential: import.meta.env.VITE_TURN_CREDENTIAL ?? '',
  },
];

// Available emoji reactions
const REACTIONS = ['👍', '👏', '😂', '❤️', '🎉', '🤔'];

// Floating reaction component
function FloatingReaction({ emoji, id, onDone }: { emoji: string; id: string; onDone: (id: string) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDone(id), 2500);
    return () => clearTimeout(timer);
  }, [id, onDone]);
  const left = 10 + Math.random() * 80;
  return (
    <div
      className="pointer-events-none absolute text-4xl z-50"
      style={{ left: `${left}%`, bottom: '120px', animation: 'floatUp 2.5s ease-out forwards' }}
    >
      {emoji}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function GuestRoomPage({ token }: { token: string }) {
  // Guests have no account: the language comes from the browser (i18n detector) unless one was saved
  const { t, i18n } = useTranslation();
  const lang: string | undefined = i18n?.language || undefined;
  // Phones: fit the page to the visible area so the keyboard never covers the chat composer
  const isPhone = useIsPhone();
  useEffect(() => (isPhone ? watchVisualViewport() : undefined), [isPhone]);
  // ── State machine: loading → preview → joined ──────────────
  const [phase, setPhase] = useState<'loading' | 'error' | 'preview' | 'joined'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [session, setSession] = useState<GuestSession | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [joining, setJoining] = useState(false);

  // ── Chat state ─────────────────────────────────────────────
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgInput, setMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // ── Video call state ───────────────────────────────────────
  const [inCall, setInCall] = useState(false);
  const [callId, setCallId] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [connectionState, setConnectionState] = useState('');
  /** Camera and mic both unavailable: joined receive-only. */
  const [mediaBlocked, setMediaBlocked] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const videoProducerRef = useRef<any>(null);
  const sendTransportRef = useRef<any>(null);
  /** Live screen-capture track while sharing; whether the camera was on before sharing */
  const screenTrackRef = useRef<MediaStreamTrack | null>(null);
  const camWasOnRef = useRef(false);
  /** producerId → remote consumer, so a 'producerClosed' from its owner can drop the track */
  const consumersByProducerRef = useRef(new Map<string, { consumer: any; userId: string }>());
  /** Remote video consumers waiting (paused on the SFU): the room shows one remote video, so
   *  receiving every camera of a big meeting only cost bandwidth and decoding */
  const waitingVideoRef = useRef<any[]>([]);
  const audioProducerRef = useRef<any>(null);
  const signalingWsRef = useRef<WebSocket | null>(null);

  // ── Active guests ──────────────────────────────────────────
  const [activeGuests, setActiveGuests] = useState<ActiveGuest[]>([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [callError, setCallError] = useState('');
  const [pendingCallId, setPendingCallId] = useState<string | null>(null);
  const [pendingCallerName, setPendingCallerName] = useState('');

  // ── Enhanced features state ────────────────────────────────
  const [callElapsed, setCallElapsed] = useState(0);
  const [handRaised, setHandRaised] = useState(false);
  const [remoteHandRaised, setRemoteHandRaised] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [floatingReactions, setFloatingReactions] = useState<{ id: string; emoji: string }[]>([]);
  const [showCallChat, setShowCallChat] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoAreaRef = useRef<HTMLDivElement>(null);

  // ── Lobby (before joining the call): camera preview + device choices ──
  const [previewCam, setPreviewCam] = useState(true);
  const [previewMic, setPreviewMic] = useState(true);
  const [previewLive, setPreviewLive] = useState(false);
  const [previewBlocked, setPreviewBlocked] = useState(false);
  const [showLobbyChat, setShowLobbyChat] = useState(false);
  const previewVideoRef = useRef<HTMLVideoElement>(null);

  // Camera preview while waiting in the lobby (video only: the mic is just a preference here)
  useEffect(() => {
    if (phase !== 'joined' || !session?.permissions.canVideo || inCall || !previewCam) return;
    if (!navigator.mediaDevices?.getUserMedia) return;
    let cancelled = false;
    let stream: MediaStream | null = null;
    navigator.mediaDevices.getUserMedia({ video: true }).then((s) => {
      if (cancelled) { s.getTracks().forEach(t => t.stop()); return; }
      stream = s;
      setPreviewBlocked(false);
      setPreviewLive(true);
      if (previewVideoRef.current) previewVideoRef.current.srcObject = s;
    }).catch(() => { if (!cancelled) setPreviewBlocked(true); });
    return () => {
      cancelled = true;
      stream?.getTracks().forEach(t => t.stop());
      setPreviewLive(false);
    };
  }, [phase, session, inCall, previewCam]);

  // ── Heartbeat ──────────────────────────────────────────────
  useEffect(() => {
    if (!session) return;
    const iv = setInterval(() => {
      guestApi('POST', '/v1/guest/session/heartbeat', undefined, session.sessionToken).catch(() => {});
    }, 30_000);
    return () => clearInterval(iv);
  }, [session]);

  // ── Poll for new messages (fallback if WebSocket misses events) ────────
  useEffect(() => {
    if (!session || inCall) return;
    const iv = setInterval(async () => {
      try {
        const msgRes = await guestApi<{ messages: Message[] }>(
          'GET', '/v1/guest/session/messages', undefined, session.sessionToken,
        );
        if (msgRes.success && msgRes.data?.messages) {
          const fetched = msgRes.data.messages.reverse();
          // Merge fetched messages with existing, dedup by ID
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = fetched.filter(m => !existingIds.has(m.id));
            if (newMsgs.length === 0) return prev;
            // Check for new call_invite in the delta
            for (const m of newMsgs) {
              if (m.type === 'call_invite') {
                try {
                  const cd = JSON.parse(m.content);
                  if (cd?.callId) { setPendingCallId(cd.callId); setPendingCallerName(cd.callerName || t('guestUi.someone', 'Someone')); }
                } catch { /* ignore */ }
              } else {
                try {
                  const p = JSON.parse(m.content);
                  if (p?.callId && p?.callType) { setPendingCallId(p.callId); setPendingCallerName(p.callerName || t('guestUi.someone', 'Someone')); }
                } catch { /* ignore */ }
              }
            }
            // Build merged list sorted by created_at
            const merged = [...prev, ...newMsgs];
            merged.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
            return merged;
          });
        }
      } catch { /* ignore */ }
    }, 5_000); // Poll every 5s while waiting in lobby
    return () => clearInterval(iv);
  }, [session, inCall]);

  // ── Step 1: Validate token ────────────────────────────────
  useEffect(() => {
    guestApi<RoomInfo>('GET', `/v1/guest/${token}`)
      .then((res) => {
        if (res.success && res.data) {
          setRoomInfo(res.data);
          if (res.data.suggestedName) setDisplayName(res.data.suggestedName);
          setPhase('preview');
        } else {
          setErrorMsg(res.error?.message ?? t('guestUi.error.invalidLink', 'Invalid or expired invite link'));
          setPhase('error');
        }
      })
      .catch(() => {
        setErrorMsg(t('guestUi.error.noConnection', 'Could not connect to Eurion. Please try again.'));
        setPhase('error');
      });
  }, [token]);

  // ── Step 2: Join ──────────────────────────────────────────
  const handleJoin = async () => {
    if (!displayName.trim()) return;
    setJoining(true);
    try {
      const res = await guestApi<GuestSession>('POST', `/v1/guest/${token}/join`, {
        displayName: displayName.trim(),
      });
      if (res.success && res.data) {
        setSession(res.data);
        setPhase('joined');
        // Load existing messages
        const msgRes = await guestApi<{ messages: Message[] }>(
          'GET', '/v1/guest/session/messages', undefined, res.data.sessionToken,
        );
        if (msgRes.success && msgRes.data?.messages) {
          const msgs = msgRes.data.messages.reverse();
          setMessages(msgs);
          // Detect latest call invite from the main app
          const lastCallInvite = [...msgs].reverse().find(m => {
            if (m.type === 'call_invite') return true;
            try { const p = JSON.parse(m.content); return !!(p?.callId && p?.callType); } catch { return false; }
          });
          if (lastCallInvite) {
            try {
              const cd = JSON.parse(lastCallInvite.content);
              setPendingCallId(cd.callId);
              setPendingCallerName(cd.callerName || t('guestUi.someone', 'Someone'));
            } catch {}
          }
        }
        // Connect WebSocket for real-time messages
        connectWebSocket(res.data);
        // Load active guests
        loadActiveGuests(res.data);
      } else {
        setErrorMsg(res.error?.message ?? t('guestUi.error.joinFailed', 'Failed to join'));
        setPhase('error');
      }
    } catch {
      setErrorMsg(t('guestUi.error.connectionFailed', 'Connection failed. Please try again.'));
      setPhase('error');
    }
    setJoining(false);
  };

  // ── WebSocket connection ──────────────────────────────────
  const connectWebSocket = useCallback((sess: GuestSession) => {
    const raw = API_BASE;
    let wsBase: string;
    if (raw.startsWith('http')) {
      wsBase = raw.replace('https://', 'wss://').replace('http://', 'ws://');
    } else {
      const p = location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsBase = `${p}//${location.host}${raw}`;
    }

    const ws = new WebSocket(`${wsBase}/v1/ws?token=${encodeURIComponent(sess.sessionToken)}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string);
        if (msg.type === 'message.new' && msg.data) {
          const msgData = msg.data as Message;
          // Detect call_invite from main app
          if (msgData.type === 'call_invite') {
            try {
              const cd = JSON.parse(msgData.content);
              if (cd?.callId) { setPendingCallId(cd.callId); setPendingCallerName(cd.callerName || t('guestUi.someone', 'Someone')); }
            } catch {}
          } else {
            try {
              const p = JSON.parse(msgData.content);
              if (p?.callId && p?.callType) { setPendingCallId(p.callId); setPendingCallerName(p.callerName || t('guestUi.someone', 'Someone')); }
            } catch {}
          }
          // Deduplicate: only append if message ID not already present
          setMessages(prev => {
            if (prev.some(m => m.id === msgData.id)) return prev;
            return [...prev, msgData];
          });
        }
        if (msg.type === 'typing' && msg.data) {
          // Could show typing indicator
        }
        if (msg.type === 'guest.joined' || msg.type === 'guest.left') {
          // Refresh guest list
          if (sess) loadActiveGuests(sess);
        }
      } catch { /* ignore */ }
    };

    ws.onclose = () => {
      // Attempt reconnect after ~3s (jittered so guests don't all return at once)
      setTimeout(() => {
        if (wsRef.current === ws) {
          connectWebSocket(sess);
        }
      }, jittered(3000));
    };
  }, []);

  // ── Load active guests ────────────────────────────────────
  const loadActiveGuests = async (sess: GuestSession) => {
    const res = await guestApi<ActiveGuest[]>(
      'GET', `/v1/guest/session/active/${sess.roomId}`, undefined, sess.sessionToken,
    );
    if (res.success && res.data) {
      setActiveGuests(res.data);
    }
  };

  // ── Send message ──────────────────────────────────────────
  const sendMessage = async () => {
    if (!msgInput.trim() || !session || sending) return;
    setSending(true);
    try {
      await guestApi('POST', '/v1/guest/session/message', {
        content: msgInput.trim(),
      }, session.sessionToken);
      setMsgInput('');
    } catch { /* ignore */ }
    setSending(false);
  };

  // ── Auto-scroll chat ─────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Video call ────────────────────────────────────────────
  const cleanupCall = useCallback(() => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    // Close mediasoup producers
    if (videoProducerRef.current) { try { videoProducerRef.current.close(); } catch {} }
    if (audioProducerRef.current) { try { audioProducerRef.current.close(); } catch {} }
    videoProducerRef.current = null;
    audioProducerRef.current = null;
    // Close mediasoup transports
    if ((window as any).__guestSendTransport) { try { (window as any).__guestSendTransport.close(); } catch {} }
    if ((window as any).__guestRecvTransport) { try { (window as any).__guestRecvTransport.close(); } catch {} }
    (window as any).__guestSendTransport = null;
    (window as any).__guestRecvTransport = null;
    (window as any).__guestDevice = null;
    if (signalingWsRef.current && signalingWsRef.current.readyState <= WebSocket.OPEN) {
      signalingWsRef.current.close();
    }
    signalingWsRef.current = null;
    setInCall(false);
    setCallId(null);
    setRemoteConnected(false);
    setConnectionState('');
    setMediaBlocked(false);
    setHandRaised(false);
    setRemoteHandRaised(false);
  }, []);

  const startCall = async (videoEnabled: boolean, existingCallId?: string, startMuted = false) => {
    if (!session) return;
    setIsVideoOn(videoEnabled);
    setIsMuted(startMuted);
    setConnectionState('starting');
    setCallError('');

    try {
      let newCallId: string;

      if (existingCallId) {
        newCallId = existingCallId;
      } else {
        setCallError(t('guestUi.call.noActiveCall', 'No active call to join. Wait for the host to start a call.'));
        setConnectionState('');
        return;
      }

      setCallId(newCallId);
      setInCall(true);

      // ── Step 1: Join the call → get mediasoup router RTP capabilities ──
      const joinRes = await guestApi<{ routerRtpCapabilities: any; callId: string }>(
        'POST', `/v1/calls/${newCallId}/join`,
        { userId: session.guestId, displayName: session.displayName },
        session.sessionToken,
      );
      if (!joinRes.success || !joinRes.data?.routerRtpCapabilities) {
        setCallError(t('guestUi.call.joinFailed', 'Failed to join call. The host may not have started it yet.'));
        cleanupCall();
        return;
      }

      // ── Step 2: Load mediasoup-client device ──
      const ms = await import('mediasoup-client');
      const device = new ms.Device();
      await device.load({ routerRtpCapabilities: joinRes.data.routerRtpCapabilities });
      (window as any).__guestDevice = device;

      // ── Step 2b: Fetch ICE servers from API (includes TURN with credentials) ──
      let iceServers: RTCIceServer[] = FALLBACK_ICE_SERVERS;
      try {
        const iceRes = await guestApi<{ iceServers: RTCIceServer[] }>(
          'GET', '/v1/calls/ice-servers', undefined, session.sessionToken,
        );
        if (iceRes.success && iceRes.data?.iceServers?.length) {
          iceServers = iceRes.data.iceServers;
        }
      } catch { /* use fallback */ }

      // ── Step 3: Get local media ──
      // If both camera and mic are blocked (permission denied, or the device
      // is busy in another app) we still join receive-only, so the guest can
      // see and hear the others instead of sitting on "Waiting…" forever.
      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: videoEnabled,
        });
      } catch {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          setIsVideoOn(false);
        } catch {
          setIsVideoOn(false);
          setIsMuted(true);
          setMediaBlocked(true);
        }
      }
      if (stream && startMuted) stream.getAudioTracks().forEach(t => { t.enabled = false; });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      // ── Step 4: Create send transport (only when we have something to send) ──
      let sendTransport: any = null;
      if (stream) {
      const stRes = await guestApi<any>(
        'POST', `/v1/calls/${newCallId}/transport`,
        { direction: 'send' },
        session.sessionToken,
      );
      if (!stRes.success || !stRes.data) {
        setCallError(t('guestUi.call.sendTransport', 'Failed to create send transport'));
        cleanupCall();
        return;
      }
      sendTransport = device.createSendTransport({
        ...stRes.data,
        iceServers,
      });
      (window as any).__guestSendTransport = sendTransport;
      sendTransportRef.current = sendTransport;

      sendTransport.on('connect', async ({ dtlsParameters }: any, callback: () => void, errback: (e: Error) => void) => {
        try {
          await guestApi('POST', `/v1/calls/${newCallId}/transport/${sendTransport.id}/connect`,
            { dtlsParameters }, session.sessionToken);
          callback();
        } catch (e: any) { errback(e); }
      });
      sendTransport.on('produce', async ({ kind, rtpParameters, appData }: any, callback: (p: any) => void, errback: (e: Error) => void) => {
        try {
          const r = await guestApi<any>('POST', `/v1/calls/${newCallId}/produce`,
            { transportId: sendTransport.id, kind, rtpParameters, appData }, session.sessionToken);
          callback({ id: r.data?.producerId });
        } catch (e: any) { errback(e); }
      });
      }

      // ── Step 5: Create recv transport ──
      const rtRes = await guestApi<any>(
        'POST', `/v1/calls/${newCallId}/transport`,
        { direction: 'recv' },
        session.sessionToken,
      );
      if (!rtRes.success || !rtRes.data) {
        setCallError(t('guestUi.call.recvTransport', 'Failed to create recv transport'));
        cleanupCall();
        return;
      }
      const recvTransport = device.createRecvTransport({
        ...rtRes.data,
        iceServers,
      });
      (window as any).__guestRecvTransport = recvTransport;

      recvTransport.on('connect', async ({ dtlsParameters }: any, callback: () => void, errback: (e: Error) => void) => {
        try {
          await guestApi('POST', `/v1/calls/${newCallId}/transport/${recvTransport.id}/connect`,
            { dtlsParameters }, session.sessionToken);
          callback();
        } catch (e: any) { errback(e); }
      });

      // ── Step 6: Produce audio + video ──
      let audioProdId: string | null = null;
      let videoProdId: string | null = null;

      const audioTrack = stream?.getAudioTracks()[0];
      if (sendTransport && audioTrack) {
        const audioProd = await sendTransport.produce({ track: audioTrack });
        audioProdId = audioProd.id;
        audioProducerRef.current = audioProd;
      }

      const videoTrack = stream?.getVideoTracks()[0];
      if (sendTransport && videoTrack) {
        const videoProd = await sendTransport.produce({ track: videoTrack });
        videoProdId = videoProd.id;
        videoProducerRef.current = videoProd;
      }

      setConnectionState('connected');

      // ── Helper: consume a remote producer ──
      const consumeProducer = async (producerId: string, _remoteUserId: string) => {
        try {
          const cr = await guestApi<any>('POST', `/v1/calls/${newCallId}/consume`, {
            transportId: recvTransport.id,
            producerId,
            rtpCapabilities: device.rtpCapabilities,
          }, session.sessionToken);
          if (!cr.success || !cr.data) return;

          const consumer = await recvTransport.consume({
            id: cr.data.consumerId,
            producerId: cr.data.producerId,
            kind: cr.data.kind,
            rtpParameters: cr.data.rtpParameters,
          });
          consumersByProducerRef.current.set(cr.data.producerId, { consumer, userId: _remoteUserId });
          // A second remote video stays paused (server-side consumers start paused) until the shown one goes
          const shown = (remoteVideoRef.current?.srcObject as MediaStream | null)?.getVideoTracks()
            .some((t) => t.readyState === 'live');
          if (consumer.kind === 'video' && shown) {
            waitingVideoRef.current.push(consumer);
            setRemoteConnected(true);
            return;
          }
          await guestApi('POST', `/v1/calls/${newCallId}/consumer/${consumer.id}/resume`,
            undefined, session.sessionToken);

          // Attach remote stream
          if (consumer.kind === 'video' && remoteVideoRef.current) {
            const existingStream = remoteVideoRef.current.srcObject as MediaStream | null;
            if (existingStream) {
              existingStream.addTrack(consumer.track);
            } else {
              remoteVideoRef.current.srcObject = new MediaStream([consumer.track]);
            }
          }
          if (consumer.kind === 'audio' && remoteAudioRef.current) {
            const existingStream = remoteAudioRef.current.srcObject as MediaStream | null;
            if (existingStream) {
              existingStream.addTrack(consumer.track);
            } else {
              remoteAudioRef.current.srcObject = new MediaStream([consumer.track]);
            }
          }
          setRemoteConnected(true);
        } catch (e) {
          console.error('Guest consume failed:', e);
        }
      };

      // Shows the next waiting remote video when the shown one is gone
      const showNextVideo = async () => {
        const el = remoteVideoRef.current;
        const st = el?.srcObject as MediaStream | null;
        if (!el || st?.getVideoTracks().some((t) => t.readyState === 'live')) return;
        let next;
        while ((next = waitingVideoRef.current.shift()) && next.closed) { /* skip closed */ }
        if (!next) return;
        await guestApi('POST', `/v1/calls/${newCallId}/consumer/${next.id}/resume`, undefined, session.sessionToken)
          .catch(() => {});
        el.srcObject = new MediaStream([...(st?.getAudioTracks() ?? []), next.track]);
        setRemoteConnected(true);
      };

      // ── Step 7: Connect signaling WS for peer events ──
      const raw = API_BASE;
      let wsBase: string;
      if (raw.startsWith('http')) {
        wsBase = raw.replace('https://', 'wss://').replace('http://', 'ws://');
      } else {
        const p = location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsBase = `${p}//${location.host}${raw}`;
      }

      const sigWs = new WebSocket(
        `${wsBase}/v1/signaling/${newCallId}?token=${encodeURIComponent(session.sessionToken)}`,
      );
      signalingWsRef.current = sigWs;

      sigWs.onopen = () => {
        // Announce our producers so existing peers know to consume us
        if (audioProdId) {
          sigWs.send(JSON.stringify({ type: 'newProducer', data: { producerId: audioProdId, userId: session.guestId, kind: 'audio', displayName: session.displayName } }));
        }
        if (videoProdId) {
          sigWs.send(JSON.stringify({ type: 'newProducer', data: { producerId: videoProdId, userId: session.guestId, kind: 'video', displayName: session.displayName } }));
        }
      };

      sigWs.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          switch (msg.type) {
            case 'newProducer': {
              const rUserId = msg.data?.userId ?? msg.from;
              if (rUserId !== session.guestId) {
                await consumeProducer(msg.data.producerId, rUserId);
              }
              break;
            }
            case 'peerLeft': {
              const peerId = msg.data?.peerId ?? msg.from;
              if (peerId !== session.guestId) {
                // Clear remote video
                if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
                if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
                setRemoteConnected(false);
                setRemoteHandRaised(false);
                void showNextVideo();
              }
              break;
            }
            case 'producerClosed': {
              const entry = consumersByProducerRef.current.get(msg.data?.producerId);
              if (!entry || !mayCloseProducer(entry.userId, msg.from)) break;
              consumersByProducerRef.current.delete(msg.data.producerId);
              try { entry.consumer.close(); } catch { /* already closed */ }
              waitingVideoRef.current = waitingVideoRef.current.filter((c) => c !== entry.consumer);
              // Drop the track so the frozen last frame (e.g. of a stopped screen share) goes away
              for (const el of [remoteVideoRef.current, remoteAudioRef.current]) {
                const st = el?.srcObject as MediaStream | null;
                if (el && st?.getTracks().includes(entry.consumer.track)) {
                  el.srcObject = new MediaStream(st.getTracks().filter(t => t !== entry.consumer.track));
                }
              }
              void showNextVideo();
              break;
            }
            case 'handRaise':
              setRemoteHandRaised(!!(msg.data?.raised));
              break;
            case 'reaction': {
              const rEmoji = msg.data?.emoji as string;
              if (rEmoji) {
                const rid = `r-${Date.now()}-${Math.random()}`;
                setFloatingReactions(prev => [...prev, { id: rid, emoji: rEmoji }]);
              }
              break;
            }
          }
        } catch (err) {
          console.error('Signaling error:', err);
        }
      };

      sigWs.onclose = () => {
        // Signaling WS closed — don't reconnect if call ended
      };

      // ── Step 8: Consume existing producers from the SFU ──
      const pRes = await guestApi<any[]>(
        'GET', `/v1/calls/${newCallId}/producers`,
        undefined, session.sessionToken,
      );
      if (pRes.success && pRes.data) {
        for (const p of pRes.data) {
          await consumeProducer(p.producerId, p.userId);
        }
      }

    } catch (err) {
      console.error('Failed to start call:', err);
      setCallError(t('guestUi.call.startFailed', 'Failed to start call. Please try again.'));
      cleanupCall();
    }
  };

  const endCall = () => {
    if (callId && session) {
      guestApi('POST', `/v1/calls/${callId}/leave`, {
        userId: session.guestId,
      }, session.sessionToken).catch(() => {});
    }
    cleanupCall();
  };

  // ── Toggle mute/video — directly manipulate tracks ──────
  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !next; });
  };

  const toggleVideoTrack = async () => {
    if (!localStreamRef.current) return;
    const next = !isVideoOn;
    setIsVideoOn(next);

    if (next) {
      // Turning camera ON — acquire a new video track and replace in producer
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const newTrack = camStream.getVideoTracks()[0];
        if (!newTrack) return;

        // Remove old video tracks from local stream
        localStreamRef.current.getVideoTracks().forEach(t => {
          localStreamRef.current!.removeTrack(t);
          t.stop();
        });
        localStreamRef.current.addTrack(newTrack);

        // Replace track in mediasoup producer
        if (videoProducerRef.current) {
          await videoProducerRef.current.replaceTrack({ track: newTrack });
          videoProducerRef.current.resume();
        } else {
          // No existing producer — create one via send transport
          const sendTransport = (window as any).__guestSendTransport;
          if (sendTransport) {
            const videoProd = await sendTransport.produce({ track: newTrack });
            videoProducerRef.current = videoProd;
            // Announce the new video producer to peers via signaling WS
            if (signalingWsRef.current?.readyState === WebSocket.OPEN && session) {
              signalingWsRef.current.send(JSON.stringify({
                type: 'newProducer',
                data: { producerId: videoProd.id, userId: session.guestId, kind: 'video', displayName: session.displayName },
              }));
            }
          }
        }

        // Update local preview
        if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
      } catch {
        setIsVideoOn(false);
      }
    } else {
      // Turning camera OFF — pause producer and stop tracks
      localStreamRef.current.getVideoTracks().forEach(t => {
        t.enabled = false;
        t.stop();
      });
      if (videoProducerRef.current) {
        videoProducerRef.current.pause();
      }
    }
  };

  /** Close our video producer on the SFU and tell the others to drop that track. */
  const closeVideoProducerGuest = async () => {
    const p = videoProducerRef.current;
    if (!p || !session) return;
    videoProducerRef.current = null;
    try { p.close(); } catch { /* already closed */ }
    if (callId) {
      try { await guestApi('POST', `/v1/calls/${callId}/producer/${p.id}/close`, undefined, session.sessionToken); } catch { /* dropped on leave */ }
    }
    if (signalingWsRef.current?.readyState === WebSocket.OPEN) {
      signalingWsRef.current.send(JSON.stringify({ type: 'producerClosed', data: { producerId: p.id, userId: session.guestId, kind: 'video' } }));
    }
  };

  // ── Restore camera helper (reused by screen share stop + onended) ──
  /** Returns false when no camera could be opened (blocked, absent). */
  const restoreCameraGuest = async (): Promise<boolean> => {
    if (!localStreamRef.current) return false;
    try {
      const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
      const camTrack = camStream.getVideoTracks()[0];
      if (!camTrack) return false;
      // Remove dead/old video tracks
      localStreamRef.current.getVideoTracks().forEach(t => {
        localStreamRef.current!.removeTrack(t);
        t.stop();
      });
      localStreamRef.current.addTrack(camTrack);
      // Replace track in mediasoup producer
      if (videoProducerRef.current) {
        await videoProducerRef.current.replaceTrack({ track: camTrack });
      }
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
      setIsVideoOn(true);
      return true;
    } catch {
      console.warn('Could not restore camera after screen share');
      return false;
    }
  };

  // ── Screen share ──────────────────────────────────────────
  // Stopping must always end the capture. Before, a guest without a camera (or with it
  // blocked) failed to restore the camera and the screen kept being shared.
  const stopScreenShareGuest = async () => {
    const st = screenTrackRef.current;
    screenTrackRef.current = null;
    if (st) {
      st.onended = null;
      st.stop();
      localStreamRef.current?.removeTrack(st);
    }
    const restored = camWasOnRef.current && await restoreCameraGuest();
    if (!restored) {
      // No camera to go back to: stop sending video so the others see the avatar
      await closeVideoProducerGuest();
      setIsVideoOn(false);
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
    }
    setIsScreenSharing(false);
  };

  const toggleScreenShare = async () => {
    if (!localStreamRef.current) return;

    if (isScreenSharing) {
      await stopScreenShareGuest();
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];
        if (screenTrack) {
          screenTrackRef.current = screenTrack;
          camWasOnRef.current = isVideoOn;
          // Replace track in mediasoup producer, or share through a new one (no camera)
          if (videoProducerRef.current) {
            await videoProducerRef.current.replaceTrack({ track: screenTrack });
          } else if (sendTransportRef.current && session) {
            videoProducerRef.current = await sendTransportRef.current.produce({ track: screenTrack });
            if (signalingWsRef.current?.readyState === WebSocket.OPEN) {
              signalingWsRef.current.send(JSON.stringify({ type: 'newProducer', data: { producerId: videoProducerRef.current.id, userId: session.guestId, kind: 'video', displayName: session.displayName } }));
            }
          }
          // Replace local stream
          localStreamRef.current.getVideoTracks().forEach(t => {
            localStreamRef.current!.removeTrack(t);
            t.stop();
          });
          localStreamRef.current.addTrack(screenTrack);
          if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
          // The browser's own "Stop sharing" button
          screenTrack.onended = () => { void stopScreenShareGuest(); };
        }
        setIsScreenSharing(true);
      } catch { /* user cancelled */ }
    }
  };

  // ── Call timer ─────────────────────────────────────────────
  useEffect(() => {
    if (!inCall) { setCallElapsed(0); return; }
    const iv = setInterval(() => setCallElapsed(e => e + 1), 1000);
    return () => clearInterval(iv);
  }, [inCall]);

  const formatElapsed = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ── Keyboard shortcuts ─────────────────────────────────────
  useEffect(() => {
    if (!inCall) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      switch (e.key.toLowerCase()) {
        case 'm': toggleMute(); break;
        case 'v': void toggleVideoTrack(); break;
        case 'h': handleToggleHand(); break;
        case 'f': handleToggleFullscreen(); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [inCall, handRaised]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fullscreen ──────────────────────────────────────────────
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const handleToggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      videoAreaRef.current?.requestFullscreen().catch(() => {});
    }
  };

  // ── Raise hand ──────────────────────────────────────────────
  const handleToggleHand = () => {
    const next = !handRaised;
    setHandRaised(next);
    if (signalingWsRef.current?.readyState === WebSocket.OPEN) {
      signalingWsRef.current.send(JSON.stringify({ type: 'handRaise', data: { raised: next } }));
    }
  };

  // ── Reactions ───────────────────────────────────────────────
  const sendReaction = (emoji: string) => {
    const rid = `r-${Date.now()}-${Math.random()}`;
    setFloatingReactions(prev => [...prev, { id: rid, emoji }]);
    if (signalingWsRef.current?.readyState === WebSocket.OPEN) {
      signalingWsRef.current.send(JSON.stringify({ type: 'reaction', data: { emoji } }));
    }
    setShowReactionPicker(false);
  };

  const removeReaction = useCallback((id: string) => {
    setFloatingReactions(prev => prev.filter(r => r.id !== id));
  }, []);

  // ── Leave room ────────────────────────────────────────────
  const leaveRoom = () => {
    cleanupCall();
    wsRef.current?.close();
    wsRef.current = null;
    setPhase('preview');
    setSession(null);
    setMessages([]);
  };

  // ── Format time ───────────────────────────────────────────
  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
  };

  // ── Parse sender display from message ─────────────────────
  const parseSender = (msg: Message) => {
    // Guest messages have format "[Guest: Name] content"
    const guestMatch = msg.content.match(/^\[Guest: (.+?)\] /);
    if (guestMatch) {
      return {
        name: guestMatch[1]!,
        content: msg.content.replace(/^\[Guest: .+?\] /, ''),
        isGuest: true,
      };
    }
    return {
      name: msg.sender_id === FACILITATOR_SENDER_ID ? FACILITATOR_NAME : (msg.sender_name || t('guestUi.chat.eurionUser', 'Eurion user')),
      content: msg.content,
      isGuest: false,
    };
  };

  const initials = (name: string) =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]!.toUpperCase()).join('') || '?';

  // ────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────

  // ── Loading ───────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center bg-nord-0">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-frost-0" />
          <p className="text-sm text-nord-4">{t('guestUi.loading', 'Loading room…')}</p>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="flex h-screen items-center justify-center bg-nord-0 px-4">
        <div className="max-w-md rounded-xl bg-nord-1 p-8 text-center shadow-2xl">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-nord-11" />
          <h2 className="mb-2 text-xl font-semibold text-nord-5">{t('guestUi.error.title', 'Cannot join room')}</h2>
          <p className="mb-6 text-sm text-nord-4">{errorMsg}</p>
          <a
            href="https://eurion.se"
            className="inline-block rounded-lg bg-frost-0 px-6 py-2.5 text-sm font-medium text-nord-0 hover:bg-frost-1 transition-colors"
          >
            {t('guestUi.goToEurion', 'Go to Eurion')}
          </a>
        </div>
      </div>
    );
  }

  // ── Preview / Join ────────────────────────────────────────
  if (phase === 'preview' && roomInfo) {
    return (
      <main className="flex h-screen items-center justify-center bg-nord-0 px-4">
        <div className="w-full max-w-md">
          {/* Logo + branding */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-frost-0/20 text-2xl">
              🇪🇺
            </div>
            <h1 className="text-2xl font-bold text-nord-5">Eurion</h1>
            <p className="mt-1 text-xs text-nord-4">{t('guestUi.tagline', 'EU-sovereign communication')}</p>
          </div>

          {/* Room info card */}
          <div className="rounded-xl bg-nord-1 p-6 shadow-2xl">
            <div className="mb-4 rounded-lg bg-nord-2 p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-nord-4">{t('guestUi.invitedTo', "You're invited to")}</p>
              <h2 className="mt-1 text-lg font-semibold text-nord-5">{roomInfo.roomName}</h2>
              {roomInfo.roomTopic && (
                <p className="mt-1 text-sm text-nord-4">{roomInfo.roomTopic}</p>
              )}
            </div>

            {/* Permissions badges */}
            <div className="mb-5 flex flex-wrap gap-2">
              {roomInfo.permissions.canChat && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-nord-2 px-3 py-1 text-xs font-medium text-nord-5">
                  <MessageSquare className="h-3.5 w-3.5 text-frost-0" aria-hidden="true" /> {t('guestUi.perm.chat', 'Chat')}
                </span>
              )}
              {roomInfo.permissions.canVideo && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-nord-2 px-3 py-1 text-xs font-medium text-nord-5">
                  <Video className="h-3.5 w-3.5 text-aurora-3" aria-hidden="true" /> {t('guestUi.perm.video', 'Video')}
                </span>
              )}
              {roomInfo.permissions.canScreenShare && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-nord-2 px-3 py-1 text-xs font-medium text-nord-5">
                  <Monitor className="h-3.5 w-3.5 text-aurora-4" aria-hidden="true" /> {t('guestUi.perm.screenShare', 'Screen share')}
                </span>
              )}
            </div>

            {/* Name input */}
            <div className="mb-4">
              <label className="mb-1.5 block text-sm font-medium text-nord-4">{t('guestUi.yourName', 'Your name')}</label>
              <input
                type="text"
                placeholder={t('guestUi.namePlaceholder', 'Enter your name…')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                maxLength={50}
                autoFocus
                className="w-full rounded-lg border border-nord-3/30 bg-nord-2 px-4 py-3 text-nord-5 placeholder-nord-muted outline-none focus:border-frost-0 focus:ring-1 focus:ring-frost-0 transition-colors"
              />
            </div>

            {/* Join button */}
            <button
              onClick={handleJoin}
              disabled={!displayName.trim() || joining}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-frost-0 px-4 py-3 text-sm font-semibold text-nord-0 hover:bg-frost-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {joining ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {t('guestUi.joinRoom', 'Join Room')}
            </button>

            {/* Security note */}
            <div className="mt-4 flex items-center gap-2 text-xs text-nord-muted">
              <Shield className="h-3.5 w-3.5" />
              <span>{t('guestUi.securityNote', 'Encrypted in transit · No account required')}</span>
            </div>
          </div>

          {/* Expiry info */}
          <p className="mt-4 text-center text-xs text-nord-muted">
            {tv(t, 'guestUi.expires', 'This link expires {{date}} at {{time}}', {
              date: new Date(roomInfo.expiresAt).toLocaleDateString(lang),
              time: new Date(roomInfo.expiresAt).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
            })}
          </p>
        </div>
      </main>
    );
  }

  // ── Joined — main room view ───────────────────────────────
  if (phase === 'joined' && session && roomInfo) {
    // ── Helper: Detect call invite messages ─────────────────
    const parseCallInvite = (msg: Message): { callId: string; callType: string; callerName: string } | null => {
      if (msg.type === 'call_invite') {
        try { return JSON.parse(msg.content); } catch { return null; }
      }
      try {
        const p = JSON.parse(msg.content);
        if (p && typeof p === 'object' && p.callId && p.callType) return p;
      } catch {}
      return null;
    };

    // ── Helper: Chat panel (reused in both layouts) ──────────
    const chatPanel = (
      <div className="flex flex-1 flex-col min-h-0">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {messages.length === 0 && (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-nord-muted">{t('guestUi.chat.empty', 'No messages yet. Say hello! 👋')}</p>
            </div>
          )}
          {messages.map((msg) => {
            // Call invites: a quiet divider line; joining happens from the lobby stage
            const callInviteData = parseCallInvite(msg);
            if (callInviteData) {
              return (
                <div key={msg.id} className="my-3 flex items-center gap-3 text-[11px] text-nord-muted">
                  <span className="h-px flex-1 bg-nord-3/30" />
                  <span className="flex items-center gap-1.5">
                    <PhoneCall className="h-3 w-3 text-aurora-3" aria-hidden="true" />
                    {tv(t, callInviteData.callType === 'video' ? 'guestUi.chat.startedVideo' : 'guestUi.chat.startedVoice',
                      callInviteData.callType === 'video' ? '{{name}} started a video call' : '{{name}} started a voice call',
                      { name: callInviteData.callerName || t('guestUi.someone', 'Someone') })} · {formatTime(msg.created_at)}
                  </span>
                  <span className="h-px flex-1 bg-nord-3/30" />
                </div>
              );
            }

            // Meeting recap from the Eurion Facilitator: a summary card
            if (msg.type === 'system' && msg.sender_id === FACILITATOR_SENDER_ID && msg.metadata?.['kind'] === 'meeting_recap') {
              return (
                <div key={msg.id} className="mb-4 rounded-xl border border-frost-0/25 bg-frost-0/5 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-frost-0/20">
                      <Sparkles className="h-3.5 w-3.5 text-frost-0" aria-hidden="true" />
                    </span>
                    <span className="text-xs font-semibold text-frost-0">{FACILITATOR_NAME}</span>
                    <span className="text-[10px] text-nord-muted">{formatTime(msg.created_at)}</span>
                  </div>
                  <MessageContent content={recapToHtml(msg.content)} format="html" />
                </div>
              );
            }

            const { name, content, isGuest: isSenderGuest } = parseSender(msg);
            const isMe = msg.sender_id === session.guestId;

            // A file shared in the meeting: guests may view and download it (file-service
            // allows it during a live guest session), never edit it
            const attachment = attachmentOf(msg.type, msg.metadata);
            if (attachment) {
              return (
                <div key={msg.id} className="mb-3 flex items-start gap-2.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-frost-0/20 text-[11px] font-semibold text-frost-0">
                    {initials(name)}
                  </div>
                  <div className="min-w-0 max-w-[85%]">
                    <p className="text-xs">
                      <span className="font-semibold text-nord-5">{name}</span>
                      <span className="ml-1.5 text-[10px] text-nord-muted">{formatTime(msg.created_at)}</span>
                    </p>
                    <AttachmentCard file={attachment} token={session.sessionToken} apiBase={API_BASE} canEdit={false} compact />
                  </div>
                </div>
              );
            }

            if (isMe) {
              return (
                <div key={msg.id} className="mb-3 flex justify-end">
                  <div className="max-w-[80%] rounded-2xl rounded-br-md bg-frost-0 px-3.5 py-2 text-nord-0">
                    <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
                    <p className="mt-0.5 text-right text-[10px] text-nord-0/60">{formatTime(msg.created_at)}</p>
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} className="mb-3 flex items-start gap-2.5">
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${isSenderGuest ? 'bg-aurora-4/20 text-aurora-4' : 'bg-frost-0/20 text-frost-0'}`}>
                  {initials(name)}
                </div>
                <div className="min-w-0 max-w-[85%]">
                  <p className="text-xs">
                    <span className="font-semibold text-nord-5">{name}</span>
                    {isSenderGuest && <span className="ml-1.5 text-[10px] text-nord-muted">{t('guestUi.guest', 'Guest')}</span>}
                    <span className="ml-1.5 text-[10px] text-nord-muted">{formatTime(msg.created_at)}</span>
                  </p>
                  <div className="mt-0.5 inline-block rounded-2xl rounded-tl-md bg-nord-2 px-3.5 py-2">
                    <MessageContent content={content} format={msg.format === 'markdown' || msg.format === 'html' ? 'html' : 'text'} />
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>

        {/* Message input */}
        {session.permissions.canChat && (
          <div className="shrink-0 border-t border-nord-3/20 bg-nord-1 px-4 py-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder={t('guestUi.chat.placeholder', 'Type a message…')}
                value={msgInput}
                onChange={(e) => setMsgInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                className="flex-1 rounded-lg border border-nord-3/30 bg-nord-2 px-4 py-2.5 text-sm text-nord-5 placeholder-nord-muted outline-none focus:border-frost-0 focus:ring-1 focus:ring-frost-0 transition-colors"
              />
              <button
                onClick={sendMessage}
                disabled={!msgInput.trim() || sending}
                className="rounded-lg bg-frost-0 p-2.5 text-nord-0 hover:bg-frost-1 disabled:opacity-50 transition-colors"
                title={t('guestUi.chat.send', 'Send')}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );

    // ── Helper: Participants side panel ──────────────────────
    const participantsPanel = showParticipants && (
      <div className="phone-sheet w-56 shrink-0 border-l border-nord-3/20 bg-nord-1 p-4 overflow-y-auto">
        <button onClick={() => setShowParticipants(false)} className="float-right -mr-2 -mt-2 flex h-11 w-11 items-center justify-center text-nord-muted hover:text-nord-5 md:hidden" aria-label={t('guestUi.closeParticipants', 'Close participants')}>✕</button>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-nord-4">
          {tv(t, 'guestUi.participantsCount', 'Participants ({{n}})', { n: activeGuests.length + 1 })}
        </h3>
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-nord-2 px-3 py-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-frost-0/20 text-xs font-bold text-frost-0">
            {session.displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-nord-5">{session.displayName}</p>
            <p className="text-[10px] text-nord-muted">{t('guestUi.youGuest', 'You (guest)')}</p>
          </div>
        </div>
        {activeGuests
          .filter(g => g.display_name !== session.displayName)
          .map(guest => (
            <div key={guest.id} className="mb-2 flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-nord-2 transition-colors">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-aurora-4/20 text-xs font-bold text-aurora-4">
                {guest.display_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-nord-5">{guest.display_name}</p>
                <p className="text-[10px] text-nord-muted">{t('guestUi.guest', 'Guest')}</p>
              </div>
            </div>
          ))}
      </div>
    );

    return (
      <div className="flex h-screen flex-col bg-nord-0 max-md:h-[var(--app-height,100dvh)] max-md:safe-top max-md:safe-x">
        {/* ── Top bar ──────────────────────────────────────── */}
        <header className="flex shrink-0 items-center justify-between border-b border-nord-3/20 bg-nord-1 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-frost-0/20 text-sm">
              🇪🇺
            </div>
            <div>
              <h1 className="text-sm font-semibold text-nord-5">{roomInfo.roomName}</h1>
              <p className="text-xs text-nord-4">
                {t('guestUi.guest', 'Guest')} · <span className="text-frost-0">{session.displayName}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!inCall && session.permissions.canVideo && (
              <button
                onClick={() => setShowLobbyChat(c => !c)}
                className={`rounded-lg p-2 transition-colors md:hidden ${showLobbyChat ? 'bg-frost-0/20 text-frost-0' : 'text-nord-4 hover:bg-nord-2'}`}
                title={t('guestUi.meetingChat', 'Meeting chat')}
                aria-label={t('guestUi.meetingChat', 'Meeting chat')}
              >
                <MessageSquare className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => { setShowParticipants(!showParticipants); if (!showParticipants && session) loadActiveGuests(session); }}
              className={`rounded-lg p-2 transition-colors ${showParticipants ? 'bg-frost-0/20 text-frost-0' : 'text-nord-4 hover:bg-nord-2'}`}
              title={t('guestUi.participants', 'Participants')}
            >
              <Users className="h-4 w-4" />
            </button>

            <button
              onClick={leaveRoom}
              className="rounded-lg p-2 text-nord-11 hover:bg-nord-11/10 transition-colors"
              title={t('guestUi.leaveRoom', 'Leave room')}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Call error banner */}
        {callError && (
          <div className="shrink-0 mx-4 mt-2 rounded-lg bg-nord-11/20 px-4 py-2 text-sm text-nord-11 flex items-center justify-between">
            <span>{callError}</span>
            <button onClick={() => setCallError('')} aria-label={t('common.close', 'Close')} className="ml-2 text-nord-11/60 hover:text-nord-11">✕</button>
          </div>
        )}

        {/* ── Main content: side-by-side when in a call ───── */}
        <main className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
          {inCall ? (
            <>
              {/* CSS for floating reactions */}
              <style>{`
                @keyframes floatUp {
                  0% { opacity: 1; transform: translateY(0) scale(1); }
                  50% { opacity: 1; transform: translateY(-120px) scale(1.3); }
                  100% { opacity: 0; transform: translateY(-250px) scale(0.8); }
                }
              `}</style>

              {/* Video area — takes most of the width */}
              <div ref={videoAreaRef} className="flex flex-1 flex-col min-w-0 min-h-0 bg-black md:min-h-0">
                {/* Call info bar */}
                <div className="flex shrink-0 items-center justify-between px-4 py-2 bg-nord-1/80 backdrop-blur-sm border-b border-nord-3/10">
                  <div className="flex items-center gap-3">
                    <div className={`h-2 w-2 rounded-full ${remoteConnected ? 'bg-aurora-3' : 'bg-nord-11'} animate-pulse`} />
                    <span className="text-xs text-nord-4">
                      {remoteConnected ? t('guestUi.call.connected', 'Connected') : connectionState === 'connecting' ? t('guestUi.call.connecting', 'Connecting…') : t('guestUi.call.waiting', 'Waiting…')}
                    </span>
                    <span className="font-mono text-xs text-nord-muted">{formatElapsed(callElapsed)}</span>
                    <TrustLine kind="call" className="hidden sm:flex" />
                  </div>
                  <button onClick={handleToggleFullscreen} className="rounded p-1 hover:bg-nord-2 transition-colors" title={isFullscreen ? t('guestUi.call.exitFullscreen', 'Exit fullscreen (F)') : t('guestUi.call.fullscreen', 'Fullscreen (F)')}>
                    <Maximize className="h-4 w-4 text-nord-4" />
                  </button>
                </div>

                {/* Video display */}
                <div className="relative flex flex-1 min-h-0 items-center justify-center">
                  <audio ref={remoteAudioRef} autoPlay />

                  {/* Floating reactions */}
                  {floatingReactions.map(r => (
                    <FloatingReaction key={r.id} id={r.id} emoji={r.emoji} onDone={removeReaction} />
                  ))}

                  {/* Remote hand raised */}
                  {remoteHandRaised && remoteConnected && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 rounded-full bg-aurora-4/90 px-4 py-2 shadow-lg animate-bounce">
                      <span className="text-lg">✋</span>
                      <span className="text-sm font-medium text-white">{t('guestUi.call.handRaised', 'Hand raised')}</span>
                    </div>
                  )}

                  {/* Remote video */}
                  <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    className={`absolute inset-0 w-full h-full object-contain transition-opacity ${remoteConnected ? 'opacity-100' : 'opacity-0'}`}
                  />

                  {mediaBlocked && (
                    <div role="alert" className="absolute top-3 left-1/2 z-30 -translate-x-1/2 max-w-md rounded-lg bg-nord-12/90 px-4 py-2 text-center text-xs font-medium text-nord-0 shadow-lg">
                      {t('guestUi.call.mediaBlocked', "Your camera and microphone are blocked, so others can't see or hear you. You can still see and hear them. Allow access in the browser's address bar and rejoin to take part.")}
                    </div>
                  )}

                  {/* Waiting state */}
                  {!remoteConnected && (
                    <div className="flex flex-col items-center gap-3 z-10">
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-nord-2 text-2xl font-bold text-frost-0">
                        {session.displayName.charAt(0).toUpperCase()}
                      </div>
                      <p className="text-sm text-nord-4 animate-pulse">
                        {connectionState === 'connecting' ? t('guestUi.call.connecting', 'Connecting…') : t('guestUi.call.waitingOthers', 'Waiting for others to join…')}
                      </p>
                    </div>
                  )}

                  {/* Local PiP */}
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`absolute bottom-3 right-3 w-36 rounded-lg border-2 border-nord-3/40 shadow-lg z-10 transition-opacity ${isVideoOn ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    style={{ transform: 'scaleX(-1)' }}
                  />

                  {/* Local hand badge */}
                  {handRaised && (
                    <div className="absolute bottom-3 left-3 z-20 flex items-center gap-1.5 rounded-full bg-aurora-4/90 px-3 py-1.5 shadow-lg">
                      <span>✋</span>
                      <span className="text-xs font-medium text-white">{t('guestUi.call.handRaised', 'Hand raised')}</span>
                    </div>
                  )}
                </div>

                {/* Reaction picker */}
                {showReactionPicker && (
                  <div className="flex shrink-0 items-center justify-center py-1">
                    <div className="flex gap-1 rounded-full bg-nord-1 border border-nord-3/30 px-3 py-2 shadow-xl">
                      {REACTIONS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => sendReaction(emoji)}
                          className="rounded-full p-1.5 text-xl hover:bg-nord-2 hover:scale-125 transition-all"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Call controls bar */}
                <div className="flex shrink-0 items-center justify-center gap-3 bg-nord-1/90 py-3 backdrop-blur-sm relative">
                  <button
                    onClick={toggleMute}
                    className={`rounded-full p-3 transition-colors ${isMuted ? 'bg-nord-11 text-white' : 'bg-nord-2 text-nord-4 hover:bg-nord-3'}`}
                    title={isMuted ? t('guestUi.call.unmute', 'Unmute (M)') : t('guestUi.call.mute', 'Mute (M)')}
                  >
                    {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={toggleVideoTrack}
                    className={`rounded-full p-3 transition-colors ${!isVideoOn ? 'bg-nord-11 text-white' : 'bg-nord-2 text-nord-4 hover:bg-nord-3'}`}
                    title={isVideoOn ? t('guestUi.call.cameraOff', 'Turn off camera (V)') : t('guestUi.call.cameraOn', 'Turn on camera (V)')}
                  >
                    {isVideoOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                  </button>
                  {session.permissions.canScreenShare && (
                    <button
                      onClick={toggleScreenShare}
                      className={`rounded-full p-3 transition-colors ${isScreenSharing ? 'bg-aurora-2 text-white' : 'bg-nord-2 text-nord-4 hover:bg-nord-3'}`}
                      title={isScreenSharing ? t('meet.stopShare', 'Stop sharing') : t('meet.shareScreen', 'Share screen')}
                    >
                      {isScreenSharing ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
                    </button>
                  )}
                  <button
                    onClick={handleToggleHand}
                    className={`rounded-full p-3 transition-colors ${handRaised ? 'bg-aurora-4/20 text-aurora-4' : 'bg-nord-2 text-nord-4 hover:bg-nord-3'}`}
                    title={handRaised ? t('guestUi.call.lowerHand', 'Lower hand (H)') : t('guestUi.call.raiseHand', 'Raise hand (H)')}
                  >
                    <Hand className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setShowReactionPicker(p => !p)}
                    className={`rounded-full p-3 transition-colors ${showReactionPicker ? 'bg-aurora-4/20 text-aurora-4' : 'bg-nord-2 text-nord-4 hover:bg-nord-3'}`}
                    title={t('guestUi.call.react', 'React')}
                  >
                    <span className="text-lg leading-none">😀</span>
                  </button>
                  <button
                    onClick={endCall}
                    className="rounded-full bg-nord-11 p-3 text-white hover:bg-red-600 transition-colors"
                    title={t('meet.endCall', 'End call')}
                  >
                    <PhoneOff className="h-5 w-5" />
                  </button>
                  {/* Chat toggle (visible on mobile only) */}
                  <button
                    onClick={() => setShowCallChat(c => !c)}
                    className={`rounded-full p-3 transition-colors md:hidden ${showCallChat ? 'bg-frost-0/20 text-frost-0' : 'bg-nord-2 text-nord-4 hover:bg-nord-3'}`}
                    title={t('guestUi.call.toggleChat', 'Toggle chat')}
                  >
                    <MessageSquare className="h-5 w-5" />
                  </button>

                  {/* Keyboard hints */}
                  <div className="absolute bottom-full left-4 mb-1 flex items-center gap-3 text-[10px] text-nord-muted/40 pointer-events-none">
                    <span><kbd className="rounded bg-nord-2/40 px-1">M</kbd> {t('callUi.keys.mute', 'mute')}</span>
                    <span><kbd className="rounded bg-nord-2/40 px-1">V</kbd> {t('callUi.keys.video', 'video')}</span>
                    <span><kbd className="rounded bg-nord-2/40 px-1">H</kbd> {t('callUi.keys.hand', 'hand')}</span>
                    <span><kbd className="rounded bg-nord-2/40 px-1">F</kbd> {t('callUi.keys.fullscreen', 'fullscreen')}</span>
                  </div>
                </div>
              </div>

              {/* Chat side panel during call — always visible on md+, toggleable on mobile */}
              <div className={`flex flex-col border-l border-nord-3/20 bg-nord-0 ${
                showCallChat
                  ? 'absolute inset-0 z-40 w-full md:relative md:w-80 md:shrink-0 md:z-auto'
                  : 'hidden md:flex md:w-80 md:shrink-0'
              }`}>
                <div className="shrink-0 border-b border-nord-3/20 bg-nord-1 px-3 py-2 flex items-center justify-between">
                  <p className="text-xs font-semibold text-nord-4 uppercase tracking-wider">{t('guestUi.chat.title', 'Chat')}</p>
                  <button onClick={() => setShowCallChat(false)} aria-label={t('guestUi.chat.close', 'Close chat')} className="text-nord-muted hover:text-nord-5 md:hidden">✕</button>
                </div>
                {chatPanel}
              </div>

              {/* Participants panel (rightmost) */}
              {participantsPanel}
            </>
          ) : session.permissions.canVideo ? (
            <>
              {/* Lobby: check camera and mic, then join (or wait for the host) */}
              <div className={`flex-1 min-h-0 overflow-y-auto ${showLobbyChat ? 'hidden md:block' : ''}`}>
                <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-8 md:py-12">
                  <div className="mb-6 text-center">
                    {pendingCallId ? (
                      <>
                        <p className="inline-flex items-center gap-2 rounded-full bg-aurora-3/15 px-3 py-1 text-xs font-medium text-aurora-3">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aurora-3 opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-aurora-3" />
                          </span>
                          {t('guestUi.lobby.inProgress', 'Meeting in progress')}
                        </p>
                        <h2 className="mt-3 text-xl font-semibold text-nord-6">{tv(t, 'guestUi.lobby.hostIn', '{{name}} is in the meeting', { name: pendingCallerName || t('guestUi.lobby.theHost', 'The host') })}</h2>
                        <p className="mt-1 text-sm text-nord-4">{t('guestUi.lobby.checkDevices', 'Check your camera and microphone, then join.')}</p>
                      </>
                    ) : (
                      <>
                        <p className="inline-flex items-center gap-2 rounded-full bg-nord-2 px-3 py-1 text-xs font-medium text-nord-4">
                          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                          {t('guestUi.lobby.notStarted', 'Not started yet')}
                        </p>
                        <h2 className="mt-3 text-xl font-semibold text-nord-6">{t('guestUi.lobby.waitingHost', 'Waiting for the host to start the meeting')}</h2>
                        <p className="mt-1 text-sm text-nord-4">{t('guestUi.lobby.waitingHint', 'You can join as soon as it starts. This page updates by itself.')}</p>
                      </>
                    )}
                  </div>

                  {/* Self preview */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl sm:aspect-video bg-nord-1 shadow-2xl ring-1 ring-nord-3/30">
                    <video
                      ref={previewVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`h-full w-full object-cover transition-opacity ${previewCam && previewLive ? 'opacity-100' : 'opacity-0'}`}
                      style={{ transform: 'scaleX(-1)' }}
                    />
                    {!(previewCam && previewLive) && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 pb-14 text-center">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-frost-0/20 text-2xl font-semibold text-frost-0">
                          {initials(session.displayName)}
                        </div>
                        <p className="text-xs text-nord-muted">
                          {!previewCam ? t('guestUi.lobby.cameraOff', 'Your camera is off')
                            : previewBlocked ? t('guestUi.lobby.cameraBlocked', "Camera blocked. Allow it in the browser's address bar so others can see you.")
                            : t('guestUi.lobby.cameraStarting', 'Starting camera…')}
                        </p>
                      </div>
                    )}
                    <div className="absolute left-3 top-3 rounded-md bg-nord-0/70 px-2 py-1 text-xs font-medium text-nord-6 backdrop-blur-sm">
                      {session.displayName}
                    </div>
                    <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2">
                      <button
                        onClick={() => setPreviewMic(m => !m)}
                        aria-pressed={!previewMic}
                        aria-label={previewMic ? t('guestUi.lobby.micOffLabel', 'Turn microphone off') : t('guestUi.lobby.micOnLabel', 'Turn microphone on')}
                        className={`rounded-full p-3 shadow-lg transition-colors ${previewMic ? 'bg-nord-2/90 text-nord-6 hover:bg-nord-3' : 'bg-nord-11 text-white'}`}
                      >
                        {previewMic ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                      </button>
                      <button
                        onClick={() => setPreviewCam(c => !c)}
                        aria-pressed={!previewCam}
                        aria-label={previewCam ? t('guestUi.lobby.camOffLabel', 'Turn camera off') : t('guestUi.lobby.camOnLabel', 'Turn camera on')}
                        className={`rounded-full p-3 shadow-lg transition-colors ${previewCam && !previewBlocked ? 'bg-nord-2/90 text-nord-6 hover:bg-nord-3' : 'bg-nord-11 text-white'}`}
                      >
                        {previewCam && !previewBlocked ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => { if (pendingCallId) void startCall(previewCam && !previewBlocked, pendingCallId, !previewMic); }}
                    disabled={!pendingCallId}
                    className="mt-6 flex w-full max-w-xs items-center justify-center gap-2 rounded-xl bg-aurora-3 px-6 py-3 text-sm font-semibold text-nord-0 shadow-lg transition-colors hover:bg-aurora-3/90 disabled:cursor-not-allowed disabled:bg-nord-2 disabled:text-nord-muted disabled:shadow-none"
                  >
                    <PhoneCall className="h-4 w-4" aria-hidden="true" />
                    {t('greenRoom.join', 'Join now')}
                  </button>
                  <p className="mt-2 text-xs text-nord-muted">
                    {previewMic ? t('guestUi.lobby.micOn', 'Microphone on') : t('guestUi.lobby.joinMuted', "You'll join muted")} · {previewCam && !previewBlocked ? t('guestUi.lobby.camOn', 'camera on') : t('guestUi.lobby.camOff', 'camera off')}
                  </p>
                  <TrustLine kind="call" className="mt-6 justify-center" />
                </div>
              </div>

              {/* Meeting chat (side panel; full screen on mobile when opened) */}
              <aside className={`${showLobbyChat ? 'flex' : 'hidden'} min-h-0 w-full flex-col max-md:flex-1 border-l border-nord-3/20 bg-nord-1/40 md:flex md:w-96 md:shrink-0`}>
                <div className="flex shrink-0 items-center justify-between border-b border-nord-3/20 px-4 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-nord-4">{t('guestUi.meetingChat', 'Meeting chat')}</p>
                  <button onClick={() => setShowLobbyChat(false)} className="flex h-11 w-11 items-center justify-center text-nord-muted hover:text-nord-5 md:hidden" aria-label={t('guestUi.chat.close', 'Close chat')}>✕</button>
                </div>
                {chatPanel}
              </aside>
              {participantsPanel}
            </>
          ) : (
            <>
              {/* Chat-only invite */}
              {chatPanel}
              {participantsPanel}
            </>
          )}
        </main>

        {/* ── Footer ────────────────────────────────────────── */}
        <footer className="shrink-0 border-t border-nord-3/20 bg-nord-1 px-4 py-1.5 max-md:pb-[max(0.375rem,env(safe-area-inset-bottom))] text-center">
          <p className="text-[10px] text-nord-muted">
            🔒 {t('guestUi.poweredBy', 'Powered by')} <a href="https://eurion.se" className="text-frost-0 hover:underline" target="_blank" rel="noopener noreferrer">Eurion</a> — {t('guestUi.tagline', 'EU-sovereign communication')}
          </p>
        </footer>
      </div>
    );
  }

  return null;
}
